import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';
import { HttpClient } from '@angular/common/http';

export type LatestTerms = {
  versions: string;
  title: string;
  content: string;
  updatedAt?: string;
};

type ViewLatestTermsResponse =
  | {
      status: 'success';
      terms: {
        versions: string;
        title: string;
        content: string;
        updatedAt?: string;
      };
    }
  | { status: 'failed'; message?: string };

@Injectable({
  providedIn: 'root',
})
export class TermsService {
  private apiUrl = environment.orderUrl;

  private latestTerms$?: Observable<LatestTerms | null>;

  constructor(private http: HttpClient) {}

  private get latestTermsUrl(): string {
    return environment.useDummyTenantData ? '/dummy/terms.json' : this.apiUrl;
  }

  /** Call backend: { action: "viewLatestTerms" } */
  viewLatestTerms(): Observable<LatestTerms | null> {
    const isDummy = environment.useDummyTenantData;

    const request$ = isDummy
      ? this.http.get<ViewLatestTermsResponse>(this.latestTermsUrl)
      : this.http.post<ViewLatestTermsResponse>(this.latestTermsUrl, {
          action: 'viewLatestTerms',
        });

    return request$.pipe(
      map((res) => {
        if (!res || res.status !== 'success') return null;

        const t = res.terms;
        if (!t) return null;

        return {
          versions: String(t.versions ?? ''),
          title: String(t.title ?? 'Terms of Service'),
          content: String(t.content ?? ''),
          updatedAt: t.updatedAt ?? undefined,
        };
      }),
      catchError(() => of(null)),
    );
  }

  prefetchLatestTerms(): Observable<LatestTerms | null> {
    if (!this.latestTerms$) {
      this.latestTerms$ = this.viewLatestTerms().pipe(shareReplay(1));
    }
    return this.latestTerms$;
  }

  clearCache() {
    this.latestTerms$ = undefined;
  }
}
